/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { initializeApp } from 'firebase/app';
import { initializeAuth, browserLocalPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
//
// Using initializeAuth with an explicit browserLocalPersistence instead of
// getAuth(): getAuth() lets the SDK auto-detect persistence, which tries
// IndexedDB first. IndexedDB can hang indefinitely (never resolve or
// reject) inside Capacitor's WebView, which silently stalls the very
// first onAuthStateChanged callback forever. Forcing localStorage-based
// persistence skips that IndexedDB probe entirely.
const auth = initializeAuth(app, {
  persistence: browserLocalPersistence
});
const db = getFirestore(app);

// Initialize App Check 
let appCheck;
if (typeof window !== 'undefined') {
  const isDevelopment = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1' ||
                        window.location.port === '8888';
  
  console.log('Current location:', window.location.hostname + ':' + window.location.port);
  console.log('Is development?', isDevelopment);
  //console.log('reCAPTCHA Site Key:', process.env.REACT_APP_RECAPTCHA_SITE_KEY);
  
  if (isDevelopment) {
    // Skip App Check in development: ReCaptchaV3Provider loads Google's
    // gapi/reCAPTCHA script, which fails to load under Capacitor's WebView
    // and isn't needed since the debug token mechanism bypasses it anyway.
    console.log('🔧 Development mode - App Check disabled');
  } else {
    console.log('🔒 Production mode - using reCAPTCHA');
    try {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true
      });
      console.log('✅ App Check initialized successfully');
    } catch (error) {
      console.error('❌ App Check initialization error:', error);
    }
  }
}


export { auth, db, app, appCheck };